/**
 * "Fechar sessão": zero out the current tab's working state. The user
 * intuition is "session closed = clean slate", so this disposes IFCs,
 * removes BCFs from the in-memory pool, drops session-restore hints,
 * clears the viewpoint workbench (everything not anchored elsewhere)
 * and forgets which BCF was active.
 *
 * What stays in IndexedDB: nothing tied to the current tab. The BCF
 * persistence store is wiped of the closed BCFs (they were a working
 * copy of the user's local files; on reopen the user re-imports the
 * `.bcfzip`). Viewpoints are session-only since Onda 6a, so the
 * whole `viewpoints` IDB is cleared.
 *
 * Beyond IFCs/BCFs/viewpoints, this also wipes per-session viewer
 * overlays: annotations, section box, measurements, work plane,
 * coordinate HUD, selection, diff panel, property popups. Without
 * this, a stale section box from the previous session would still
 * clip the empty scene, anotações would still hang in the air, etc.
 *
 * Caller is responsible for any URL navigation, usually
 * `navigate(/{ws}/)`, so the session indicator can be reused from
 * any page.
 */
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { viewpointsStore } from '@viewer/lib/viewpointsStore'
import { measureApi } from '@viewer/measure/measureApi'
import { ifcPersistence } from '@core/session/ifcPersistence'
import { fragmentPersistence } from '@core/session/fragmentPersistence'
import { resetRecovery } from '@core/session/recovery'
import { pointCloudApi } from '@viewer/point-cloud/pointCloudApi'
import { ifcxApi } from '@viewer/ifcx/ifcxApi'
import { limparRealce } from '@modules/bcf/lib/bcfRealce'
import { esquecerSessaoActual } from '@core/sessoes/sessaoPartilhadaEstado'

export async function closeSession(): Promise<void> {
  const store = useAppStore.getState()

  // 1. Clear viewer overlays FIRST, while IFCs/scene/world are still
  //    alive. Disposing IFCs first then asking the section box or the
  //    measure tool to reset can crash because their internals
  //    reference Three.js objects that were just disposed. Each block
  //    is best-effort: a missing slice action never blocks the rest.
  try {
    if (measureApi.hasTools()) measureApi.clearAll()
  } catch (err) {
    console.warn('[closeSession] measure clearAll failed', err)
  }
  try {
    store.setMeasureMode(null)
    store.setMeasureDeleteMode(false)
    store.setWorkPlane(null)
    store.setWorkPlanePickMode(false)
    store.setCoordsReference(null)
    store.setCoordsOriginPickMode(false)
    store.setShowCoordinates(false)
    store.setCursorWorld(null)
  } catch (err) {
    console.warn('[closeSession] measure slices reset failed', err)
  }
  try {
    store.setSectionEnabled(false)
    store.resetSection()
    // Os planos de corte soltos não são da caixa: o resetSection não lhes
    // toca, e ficavam a cortar o modelo seguinte (e o modo "plano pela
    // face" ficava armado).
    store.tirarTodosOsPlanos()
  } catch (err) {
    console.warn('[closeSession] section reset failed', err)
  }
  try {
    store.clearAnnotations()
    store.setAnnotationPlaceMode(null)
    store.setAnnotationDraftFirstPoint(null)
  } catch (err) {
    console.warn('[closeSession] annotations reset failed', err)
  }
  try {
    store.clearSelection()
    store.clearSelectionHistory()
    // O histórico de desfazer aponta para modelos e sessões de módulos
    // que deixam de existir; ficava a actuar sobre o que se abrisse a
    // seguir com o mesmo nome.
    store.clearHistory()
    store.setHover(null)
  } catch (err) {
    console.warn('[closeSession] selection reset failed', err)
  }
  try {
    store.setIfcDiffOpen(false)
    store.setIdsPanelOpen(false)
    // Os painéis do pacote de extensões fecham-se com os outros. Sem
    // isto ficavam abertos sobre uma cena vazia, a mostrar o modelo que
    // se acabou de largar.
    store.closeExtraPanels()
  } catch {
    /* ignore */
  }

  // 1b. Estado dos módulos que é derivado dos IFCs desta sessão. Sem
  //     isto, fechar a sessão e abrir a Tabela / o BoQ mostrava linhas
  //     de um modelo que já não existe, o IFCEdit guardava edições
  //     penduradas em GUIDs de ninguém e o IDS mostrava um relatório de
  //     validação de um IFC que já ninguém tinha carregado.
  try {
    store.resetInfoTable()
  } catch (err) {
    console.warn('[closeSession] resetInfoTable failed', err)
  }
  try {
    const boq = store.boqSession
    if (boq) boq.close()
    store.resetBoq()
  } catch (err) {
    console.warn('[closeSession] resetBoq failed', err)
  }
  try {
    store.resetIfcEdit()
  } catch (err) {
    console.warn('[closeSession] resetIfcEdit failed', err)
  }
  try {
    store.resetIdsModule()
  } catch (err) {
    console.warn('[closeSession] resetIdsModule failed', err)
  }
  // A conversa do assistente descreve o modelo que se está a largar, e
  // com ele deixa de fazer sentido.
  try {
    store.resetAssistant()
  } catch (err) {
    console.warn('[closeSession] resetAssistant failed', err)
  }
  // Conflitos: os resultados eram de modelos que já não existem e os
  // pareceres de triagem vão com o `resetRecovery` lá em baixo (é ele
  // que apaga a chave de localStorage). Sem isto, fechar a sessão e
  // reabrir o painel mostrava uma lista de conflitos de um IFC que já
  // ninguém tinha carregado, o esquecimento que o BoQ e o IFCEdit já
  // tinham pago uma vez.
  try {
    store.resetClash()
  } catch (err) {
    console.warn('[closeSession] resetClash failed', err)
  }
  // As cores dos elementos de um tópico BCF são um overlay com o
  // fantasma por baixo: saem com a sessão, e o fantasma volta ao que
  // estava.
  try {
    await limparRealce()
  } catch (err) {
    console.warn('[closeSession] limparRealce failed', err)
  }
  try {
    pointCloudApi.clear()
  } catch (err) {
    console.warn('[closeSession] pointCloud clear failed', err)
  }
  // O IFC5 vive fora da lista de modelos, como as nuvens: sem isto
  // ficava desenhado (e no painel) depois da "lousa limpa".
  try {
    ifcxApi.fecharTudo()
  } catch (err) {
    console.warn('[closeSession] ifcx fecharTudo failed', err)
  }
  try {
    const open = useAppStore.getState().popups
    for (const p of open) store.closePopup(p.id)
  } catch {
    /* ignore */
  }

  // 2. Dispose loaded IFCs (Fragments cleanup is async).
  const ids = store.models.map((m) => m.id)
  for (const id of ids) {
    try {
      await viewerApi.disposeModel(id)
    } catch (err) {
      console.warn('[closeSession] disposeModel failed', id, err)
    }
  }

  // 3. Drop "expected models" banner.
  store.setSessionRestoreModels(null)

  // 4. Drop every BCF from the in-memory pool. bcfClose deletes from
  //    bcfPersistence too, so reload starts fresh.
  for (const bcf of [...store.bcfLoaded]) {
    store.bcfClose(bcf.localId)
  }
  store.bcfSetActive(null)

  // 5. Wipe all viewpoints. Onda 6a: viewpoints are session-only:
  //    they don't survive across "close session" any more, regardless
  //    of attachment, because attachments live in the BCFs we just
  //    cleared.
  try {
    await viewpointsStore.clearAll()
    const refresh = useAppStore.getState().refreshViewpoints
    if (typeof refresh === 'function') void refresh()
  } catch (err) {
    console.warn('[closeSession] viewpoints clearAll failed', err)
  }

  // 6. Wipe the IFC byte cache. The cache is otherwise per-browser
  //    and accumulates across sessions, which surfaces as ghost
  //    entries in the Diagnostic panel ("ainda me apare aqui um
  //    ficheiro de outra sessão"). "Fechar sessão" is the user's
  //    explicit "clean slate" gesture; the cache goes with it. A
  //    follow-up Open Session re-primes whatever IFCs the zip
  //    snapshot carries, so auto-restore continues to work.
  try {
    await ifcPersistence.clearAll()
  } catch (err) {
    console.warn('[closeSession] ifcPersistence clearAll failed', err)
  }
  try {
    await fragmentPersistence.clearAll()
  } catch (err) {
    console.warn('[closeSession] fragmentPersistence clearAll failed', err)
  }

  // 7. Rede de recuperação: o gesto "lousa limpa" leva o buffer todo e
  //    o manifesto. Sem isto, "fechar sessão" seguido de recarregar
  //    ainda perguntava se queríamos recuperar o que acabámos de
  //    fechar. Apanha também as vistas 2D e os rascunhos, que vivem em
  //    localStorage e escapavam a esta limpeza.
  try {
    await resetRecovery()
  } catch (err) {
    console.warn('[closeSession] resetRecovery failed', err)
  }

  // 8. A sessão partilhada em que este separador estava: outra sessão
  //    não é ela, e o menu volta a oferecer "Partilhar sessão". A
  //    partilha no servidor fica como estava.
  esquecerSessaoActual()
}

/**
 * Removes from `viewpointsStore` every viewpoint whose id is not
 * referenced by `viewpointRefs` of any topic in any loaded BCF.
 * Manual hygiene tool exposed by the viewpoints panel, used to free
 * IDB space without ending the session.
 */
export async function pruneOrphanViewpoints(): Promise<number> {
  const store = useAppStore.getState()
  const attached = new Set<string>()
  for (const bcf of store.bcfLoaded) {
    for (const v of bcf.versions) {
      for (const t of v.parsed.topics) {
        for (const ref of t.viewpointRefs) attached.add(ref)
      }
    }
  }
  try {
    const all = await viewpointsStore.list()
    let removed = 0
    for (const vp of all) {
      if (!attached.has(vp.id)) {
        await viewpointsStore.remove(vp.id)
        removed++
      }
    }
    if (removed > 0) {
      console.info(`[closeSession] removed ${removed} orphan viewpoint(s)`)
    }
    try {
      const refresh = useAppStore.getState().refreshViewpoints
      if (typeof refresh === 'function') void refresh()
    } catch {
      /* slice not present, ignore */
    }
    return removed
  } catch (err) {
    console.warn('[closeSession] viewpoint prune failed', err)
    return 0
  }
}
